/*
 * THE TOURNAMENT SUMMARY AT THE TOP OF UPCOMING (multi-list build item 5).
 *
 * One row per tournament on the page: its name, how many tracked fencers are
 * entered, and the dates those fencers actually compete. A reader opening
 * Upcoming wants "what is coming, for how many of mine, and when" before they
 * want any one event's start time, and until now that answer had to be assembled
 * by scrolling.
 *
 * THIS IS NOT THE DIGEST, and the distinction is the whole reason the contents
 * are fixed rather than "whatever is useful". Direction §10's "Registration
 * closing" panel is deadline-led, amber, and carries a Register pill per row;
 * §14 defers it because it needs expectation data — knowing a fencer SHOULD be
 * at a tournament — which the product does not have and cannot infer. So this
 * carries NO deadline, no urgency treatment, no Register affordance and no
 * "not registered" row. Every one of those would be the digest approximated from
 * data that cannot support it, which is exactly what Upcoming.tsx's own header
 * comment forbids. The count and the dates are the row's only facts.
 *
 * THE DATES ARE THE FENCERS' DAYS, NOT THE TOURNAMENT'S SPAN (Kevin, with the
 * item). The October NAC runs 9–12 October; if your two fencers are in events on
 * the 9th and the 11th, this row says "9 Oct – 11 Oct". The tournament's own
 * start and end dates are already on its band below, and repeating them here
 * would answer a question nobody asked — "when is the tournament" — in the slot
 * that should answer "when do I need to be there".
 *
 * COUNTED FROM THE VISIBLE EVENTS, which is why this takes the ALREADY-GROUPED
 * tournament axis rather than the payload. `visibleUpcoming`'s done cascade has
 * run by then (decisions 357, 449), so a fencer who has finished is out of both
 * the summary and the cards below. Handed the raw payload instead, this would
 * quietly disagree with the page beneath it as the day wore on — the failure
 * mode where nobody can tell which of the two is lying.
 *
 * THE SUMMARY FOLLOWS THE GROUPING AXIS (decision 526, amending 522's "renders
 * identically on both axes"). A control that only regroups the page below it
 * was the original reasoning for holding the summary fixed on the tournament
 * set — but Kevin's instruction, confirmed when asked directly, is the
 * opposite: a page grouped by fencer wants a summary that describes fencers.
 * The rule underneath survives and is what this still serves — the summary
 * describes exactly the rows on the page below it — so `UpcomingSummary`
 * renders `groups.tournament` on the tournament axis and the new
 * `UpcomingFencerSummary` renders `groups.fencer` on the fencer axis, each
 * counting only its own rows.
 *
 * REAL ANCHORS to the ids Upcoming already puts on each tournament — one per
 * tournament, on the first appearance on whichever axis is rendering (see
 * `anchorID` there, built for decision 402's alert destinations). Reusing them
 * is not a shortcut: a second id per tournament would make `#tournament-<id>`
 * resolve to whichever the browser saw first and the document would stop being
 * valid. Plain fragment links, so the browser's own scrolling does the work and
 * the position is in the URL (build rule 10).
 */

import { useTranslation } from "react-i18next";

import { dateLocale, dateRange, firstLast } from "../lib/format";
import { TournamentFlag } from "./TournamentFlag";
import { fencerCounts, tournamentCounts } from "../lib/summary";
import type { PersonRef } from "../lib/regroup";
import styles from "./UpcomingSummary.module.css";

/**
 * The shape this counts over: `regroup`'s tournament axis, narrowed to the four
 * fields a summary reads.
 *
 * Deliberately narrower than `UpcomingTournamentBody`, the way lib/summary.ts is,
 * so the tests need no full fixture and the component cannot start reading a
 * deadline field it is not allowed to render.
 */
interface SummaryEvent<F extends PersonRef> {
  event: { event_date?: string | null | undefined };
  fencers: F[] | null;
}

export interface SummaryTournament<F extends PersonRef> {
  tournament: {
    tournament_id: string;
    name: string;
    non_designated?: boolean;
    /** ISO 3166-1 alpha-2; a non-US tournament's flag follows its name (decision 776). */
    country?: string | null | undefined;
  };
  events: SummaryEvent<F>[];
}

/**
 * The min and max `event_date` across a tournament's visible events.
 *
 * Compared as STRINGS, which is exact rather than lazy: these are date-only
 * values ("2026-10-09"), and ISO date-only strings sort lexicographically in
 * chronological order. Parsing them to Date first would buy nothing and would
 * re-introduce the zone question decision 316 keeps out of date-only values.
 *
 * An event with no published date contributes nothing — `event_date` is optional
 * on the wire — and a tournament where none of them has one gets no dates cell at
 * all rather than an invented span.
 */
function competingDays<F extends PersonRef>(
  events: SummaryEvent<F>[],
): { first: string; last: string } | null {
  let first: string | null = null;
  let last: string | null = null;

  for (const { event } of events) {
    const date = event.event_date;
    if (!date) continue;
    if (first === null || date < first) first = date;
    if (last === null || date > last) last = date;
  }

  return first !== null && last !== null ? { first, last } : null;
}

export interface UpcomingSummaryProps<F extends PersonRef> {
  /**
   * The visible tournaments, in the server's order — `regroup(...).tournament`.
   * Never re-sorted here: the server sends Upcoming chronologically and decision
   * 315's rule is that no view re-sorts what it was sent.
   */
  tournaments: SummaryTournament<F>[];
  locale?: string;
}

export function UpcomingSummary<F extends PersonRef>({
  tournaments,
  locale = dateLocale(),
}: UpcomingSummaryProps<F>) {
  const { t } = useTranslation();

  // Nothing on an empty page, and no empty heading announcing it. A summary of
  // nothing is chrome describing an absence the empty state already states
  // (build rule 2).
  if (tournaments.length === 0) return null;

  return (
    /*
     * A NAV, because this is a table of contents for the page below it, and it
     * needs an accessible name: a screen reader listing landmarks otherwise
     * announces an unnamed navigation region above the one the app shell already
     * has.
     */
    <nav className={styles.summary} aria-label={t("upcoming.summaryLabel")}>
      <ul className={styles.list}>
        {tournaments.map(({ tournament, events }) => {
          const { fencers } = tournamentCounts(events);
          const days = competingDays(events);
          const dates = days ? dateRange(days.first, days.last, locale) : null;

          return (
            <li key={tournament.tournament_id}>
              <a className={styles.row} href={`#tournament-${tournament.tournament_id}`}>
                {/*
                  A NON-DESIGNATED MEET says so under its name, in the dates'
                  size and colour (Kevin, 2026-10-14): a fact about the meet,
                  read with the name rather than with the counts.
                */}
                <span className={styles.nameCell}>
                  <span className={styles.name}>
                    {tournament.name}
                    <TournamentFlag country={tournament.country} />
                  </span>
                  {tournament.non_designated ? (
                    <span className={styles.sub}>{t("upcoming.nonDesignated")}</span>
                  ) : null}
                </span>
                {/*
                  THE COUNT AND THE DATES SHARE ONE TRAILING CELL, stacked
                  (decision 526 amending 522's three aligned columns): the
                  tournament name is the scanned datum and a phone was losing
                  width to two trailing columns for it.
                */}
                <span className={styles.meta}>
                  {/*
                    UNDER A SEARCH THIS COUNTS WHAT THE SERVER RETURNED, which is
                    the filtered payload: search on this view is server-side
                    (decision 329), so the page holds only the matching
                    registrations and the summary describes the same rows the
                    reader is looking at. The alternative — counting an unfiltered
                    payload the client does not have — would need a second request
                    and would put two different numbers about one tournament on one
                    screen.
                  */}
                  <span className={styles.fencers}>
                    {/*
                      `summary.fencers`, the SHARED count fragment the band
                      summaries already use, rather than a second copy of
                      "{{count}} fencer(s)" under `upcoming`. It is the same
                      sentence about the same thing, and two keys holding one
                      string is how the two come to disagree in the seventh locale.
                    */}
                    {t("summary.fencers", { count: fencers })}
                  </span>
                  {/* Absent rather than blank when no event has a published date:
                      the tournament's own span is NOT the fallback, since it is a
                      different fact (see the header comment). */}
                  {dates ? <span className={styles.dates}>{dates}</span> : null}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * THE FENCER-AXIS SUMMARY (decision 526). Lives in this file rather than a
 * new one, because `literals.test.ts`'s `FILES` list (decision 423) names this
 * path and a second component file would silently leave the rule.
 *
 * One row per fencer band, in `regroup(...).fencer`'s own order — alphabetical
 * by the stored surname-first name (decision 326) — never re-sorted here, the
 * same rule the tournament axis follows for the server's chronological order.
 * The count is the total number of EVENTS the fencer is registered for, not
 * the number of tournaments: a fencer in two events at one tournament and one
 * at another reads "3 events", because that is the number that answers "how
 * much is on this person's plate", and the tournament count is already the
 * band count by construction (one row per tournament below it).
 */
export interface UpcomingFencerSummaryProps<F> {
  /** `regroup(...).fencer`, in the order it came. Never re-sorted here. */
  fencers: { key: string; name: string; tournaments: { events: F[] }[] }[];
  /** Band key (`personKey`'s value) to the element id on the page below. */
  anchors: Map<string, string>;
}

export function UpcomingFencerSummary<F>({
  fencers,
  anchors,
}: UpcomingFencerSummaryProps<F>) {
  const { t } = useTranslation();

  // Nothing on an empty page, same as the tournament axis (build rule 2).
  if (fencers.length === 0) return null;

  return (
    <nav className={styles.summary} aria-label={t("upcoming.fencerSummaryLabel")}>
      <ul className={styles.list}>
        {fencers.map((group) => {
          const anchor = anchors.get(group.key);
          // A band whose key has no anchor renders no row rather than a
          // broken link. Cannot happen when both are built from the same
          // `groups.fencer` array — it is one line to be safe.
          if (!anchor) return null;

          const { events } = fencerCounts(group.tournaments);

          return (
            <li key={group.key}>
              <a className={styles.row} href={`#${anchor}`}>
                {/* DISPLAY flips to First Last; the sort stays on the stored
                    surname-first string (decision 358). */}
                <span className={styles.name}>{firstLast(group.name)}</span>
                <span className={styles.events}>
                  {t("summary.events", { count: events })}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
